import { useEffect, useState } from 'react';
import { Loader2, Mail, SendHorizontal, X } from 'lucide-react';

import { cn } from '../../utils/cn';
import { confirmEmailDraft, cancelEmailDraft } from '../../services/api/ai';
import type { EmailDraftCardData } from '../../types/ai';

type Props = {
  card: EmailDraftCardData;
  sessionId: string;
  interactive: boolean;
  onResolved: (
    message: string,
    status: 'confirmed' | 'already_sent' | 'cancelled',
    sessionClosed?: boolean,
  ) => void;
};

const ACTIVE_STATUSES = new Set(['drafted', 'editing', 'awaiting_email']);

export function EmailDraftCard({ card, sessionId, interactive, onResolved }: Props) {
  const [subject, setSubject] = useState(card.draft.subject);
  const [body, setBody] = useState(card.draft.body);
  const [email, setEmail] = useState('');
  const [emailPrompt, setEmailPrompt] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<'send' | 'cancel' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSubject(card.draft.subject);
    setBody(card.draft.body);
  }, [card.draft.id, card.draft.subject, card.draft.body]);

  const isResolved = card.draft.status === 'sent' || card.draft.status === 'cancelled';
  const editable =
    interactive && !isResolved && ACTIVE_STATUSES.has(card.draft.status) && !submitting;

  const handleSend = async () => {
    setSubmitting('send');
    setError(null);
    try {
      const response = await confirmEmailDraft({
        sessionId,
        subject,
        body,
        ...(emailPrompt && email.trim() ? { email: email.trim() } : {}),
      });
      if (response.status === 'confirmed' || response.status === 'already_sent') {
        onResolved(response.message, response.status, response.sessionClosed === true);
        return;
      }
      if (response.status === 'needs_email') {
        setEmailPrompt(response.message);
        return;
      }
      setError(card.labels.staleError);
    } catch {
      setError(card.labels.networkError);
    } finally {
      setSubmitting(null);
    }
  };

  const handleCancel = async () => {
    setSubmitting('cancel');
    setError(null);
    try {
      const response = await cancelEmailDraft({ sessionId });
      if (response.status === 'cancelled') {
        onResolved(response.message, 'cancelled');
        return;
      }
      setError(card.labels.staleError);
    } catch {
      setError(card.labels.networkError);
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="w-full max-w-xl overflow-hidden rounded-sm border border-black/10 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-black/10 bg-brand-beige-tint-1 px-4 py-2.5">
        <Mail className="h-4 w-4 shrink-0 text-brand-blue" />
        <p className="text-sm font-medium text-brand-blue">{card.labels.title}</p>
      </div>

      <div className="flex flex-col gap-3 px-4 py-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-text-grey">
            {card.labels.subjectLabel}
          </span>
          <input
            type="text"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            disabled={!editable}
            maxLength={200}
            className={cn(
              'rounded-sm border border-black/15 bg-white px-3 py-2 text-sm text-brand-blue',
              'focus:border-brand-blue focus:outline-none',
              !editable && 'bg-black/5 text-text-grey',
            )}
          />
        </label>

        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          disabled={!editable}
          rows={9}
          maxLength={4000}
          className={cn(
            'w-full resize-y rounded-sm border border-black/15 bg-white px-3 py-2 text-sm leading-6 text-brand-blue',
            'focus:border-brand-blue focus:outline-none',
            !editable && 'bg-black/5 text-text-grey',
          )}
        />

        {emailPrompt && !isResolved ? (
          <div className="flex flex-col gap-2 rounded-sm border border-brand-blue/25 bg-brand-blue/5 px-3 py-2.5">
            <p className="text-sm text-brand-blue">{emailPrompt}</p>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={!editable}
              placeholder={card.labels.emailPlaceholder}
              className="rounded-sm border border-black/15 bg-white px-3 py-2 text-sm text-brand-blue focus:border-brand-blue focus:outline-none"
            />
          </div>
        ) : null}

        {error ? <p className="text-sm text-red-700">{error}</p> : null}

        {!isResolved ? (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={!editable}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-sm border border-black/20 bg-white px-3 py-2 text-xs uppercase tracking-wider text-text-grey',
                'transition-colors hover:border-black/40 hover:text-brand-blue disabled:cursor-not-allowed disabled:opacity-60',
              )}
            >
              {submitting === 'cancel' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <X className="h-3.5 w-3.5" />
              )}
              {card.labels.cancel}
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={!editable || subject.trim().length === 0 || body.trim().length === 0}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-sm border border-brand-blue bg-brand-blue px-4 py-2 text-xs uppercase tracking-wider text-white',
                'transition-colors hover:bg-white hover:text-brand-blue disabled:cursor-not-allowed disabled:opacity-60',
              )}
            >
              {submitting === 'send' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <SendHorizontal className="h-3.5 w-3.5" />
              )}
              {card.labels.send}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
